// Společné kousky sekce Analytika (22. 9. 2026): výběr období, formátování
// a načtení dat z /api/analytika. Vzhled je ze vzorníku adminu, nic vlastního.
import React, { useMemo, useState } from "react";
import { useApiData } from "../../lib/api.js";
import { RozbalovaciVyber } from "../../components/ui.jsx";

const dnesPraha = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Prague" }).format(new Date());
const posun = (den, dni) => {
  const d = new Date(`${den}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dni);
  return d.toISOString().slice(0, 10);
};
const MESICE = ["ledna", "února", "března", "dubna", "května", "června", "července", "srpna", "září", "října", "listopadu", "prosince"];
export const datumText = (d) => {
  const x = new Date(`${d}T12:00:00Z`);
  return `${x.getUTCDate()}. ${MESICE[x.getUTCMonth()]}`;
};

const datumKratce = (d) => { const x = new Date(`${d}T12:00:00Z`); return `${x.getUTCDate()}. ${x.getUTCMonth() + 1}.`; };
/** Tvar slova podle počtu: 1 klik, 2 kliky, 5 kliků. */
export const tvar = (pocet, jedna, dveAzCtyri, pet) => {
  const x = Math.abs(Math.round(Number(pocet) || 0));
  return x === 1 ? jedna : x >= 2 && x <= 4 ? dveAzCtyri : pet;
};

/** Období k výběru. Vrací { od, do }. */
export const OBDOBI = [
  { key: "7", label: "Posledních 7 dní", rozsah: () => ({ od: posun(dnesPraha(), -6), do: dnesPraha() }) },
  { key: "minuly-tyden", label: "Minulý týden", rozsah: () => {
    const dnes = dnesPraha();
    const den = (new Date(`${dnes}T12:00:00Z`).getUTCDay() + 6) % 7;
    const pondeli = posun(dnes, -den);
    return { od: posun(pondeli, -7), do: posun(pondeli, -1) };
  } },
  { key: "30", label: "Posledních 30 dní", rozsah: () => ({ od: posun(dnesPraha(), -29), do: dnesPraha() }) },
  { key: "mesic", label: "Tento měsíc", rozsah: () => ({ od: `${dnesPraha().slice(0, 7)}-01`, do: dnesPraha() }) },
  { key: "minuly-mesic", label: "Minulý měsíc", rozsah: () => {
    const prvni = `${dnesPraha().slice(0, 7)}-01`;
    const konec = posun(prvni, -1);
    return { od: `${konec.slice(0, 7)}-01`, do: konec };
  } },
];

const KLIC = "fw40.analytika.obdobi";

/** Vybrané období, pamatuje se v prohlížeči napříč obrazovkami Analytiky. */
export function useObdobi() {
  const [klic, setKlic] = useState(() => {
    try { return localStorage.getItem(KLIC) || "7"; } catch { return "7"; }
  });
  const zmen = (k) => {
    setKlic(k);
    try { localStorage.setItem(KLIC, k); } catch { /* nevadí */ }
  };
  const volba = OBDOBI.find((o) => o.key === klic) || OBDOBI[0];
  const rozsah = useMemo(() => volba.rozsah(), [volba]);
  // Krátké datum (16. 9.), dlouhé se na telefonu ve výběru uřízlo (audit 22. 9. 2026).
  return { klic: volba.key, zmen, rozsah, popis: `${volba.label} (${datumKratce(rozsah.od)} až ${datumKratce(rozsah.do)})` };
}

export function VyberObdobi({ obdobi }) {
  return (
    <RozbalovaciVyber
      stitek="Období"
      aria-label="Období"
      value={obdobi.klic}
      onChange={obdobi.zmen}
      items={OBDOBI.map((o) => ({ key: o.key, label: o.key === obdobi.klic ? obdobi.popis : o.label }))}
    />
  );
}

/** Data Analytiky pro období. */
export function useAnalytika(rozsah, sekce = [], aktivni = true) {
  const s = sekce.length ? `&sekce=${encodeURIComponent(sekce.join(","))}` : "";
  // aktivni = false nic nenačítá (Přehled v režimu Živě).
  return useApiData(aktivni ? `/api/analytika?od=${rozsah.od}&do=${rozsah.do}${s}` : null);
}

const nbsp = (t) => String(t).replace(/\s/g, " ");
export const cislo = (v) => (v == null ? "—" : nbsp(Math.round(Number(v) || 0).toLocaleString("cs-CZ")));
export const kc = (v) => (v == null ? "—" : `${cislo(v)} Kč`);

/** Změna proti předchozímu období jako text „+18 %". */
export function zmena(ted, pred) {
  if (pred == null || ted == null || !Number(pred)) return null;
  const p = Math.round(((Number(ted) - Number(pred)) / Number(pred)) * 100);
  return `${p > 0 ? "+" : ""}${p} %`;
}

/** Kolik kontaktů nebo nákupů, česky podle počtu. */
export function vysledekText(pocet, typ) {
  const n = Number(pocet) || 0;
  if (typ === "lead") return `${n} ${n === 1 ? "kontakt" : n >= 2 && n <= 4 ? "kontakty" : "kontaktů"}`;
  return `${n} ${n === 1 ? "nákup" : n >= 2 && n <= 4 ? "nákupy" : "nákupů"}`;
}

export const POPISY_SEKCI = {
  hero: "Úvod", problem: "Problém", "co-vas-ceka": "Co vás čeká", terminy: "Termíny a lokality",
  prubeh: "Jak to probíhá", video: "Video", videohodnoceni: "Videohodnocení", "pro-koho": "Pro koho",
  galerie: "Galerie", trener: "Trenér", nabidka: "Ceník", faq: "Časté otázky", zaver: "Závěr",
  metoda: "Metoda", benefity: "Přínosy", motivace: "Motivace", "co-ziskate": "Co získá", citat: "Citát",
  bonusy: "Bonusy", reference: "Reference", "pro-koho-neni": "Pro koho není", garance: "Garance",
  autor: "O autorovi", shrnuti: "Shrnutí", formular: "Formulář", jistoty: "Jistoty",
};
